import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useRef, useState } from 'react';
import { Link } from 'react-router';

import {
  isNicknameReady,
  joinTimeSink,
  type MemberProfile,
  NicknameField,
  useSession,
} from '@/entities/session';
import { useTRPC } from '@/shared/api';
import { AUTOMATION_LEVELS, OTHER, TIME_SINKS } from '@/shared/config';

/** 회원이 커뮤니티에 표시할 정보와 온보딩 응답을 계정에서 수정한다. */
export function ProfileSection(): ReactElement | null {
  const member = useSession((state) => state.member);
  const [profileNotice, setProfileNotice] = useState('');
  const [isEditingProfile, setEditingProfile] = useState(false);
  const [draft, setDraft] = useState<MemberProfile | null>(null);
  const isSavingProfile = useRef(false);
  const profileOwner = useRef<string | null>(null);
  const trpc = useTRPC();
  const { mutateAsync: saveProfile, isPending: isProfilePending } = useMutation(
    trpc.auth.completeOnboarding.mutationOptions(),
  );

  if (!member) return null;

  const startProfileEdit = () => {
    setDraft({ ...member });
    profileOwner.current = member.email;
    setProfileNotice('');
    setEditingProfile(true);
  };

  const patchDraft = (patch: Partial<MemberProfile>) =>
    setDraft((previous) => (previous ? { ...previous, ...patch } : previous));

  const submitProfile = async () => {
    if (!draft || isSavingProfile.current || profileOwner.current !== member.email) return;
    isSavingProfile.current = true;
    setProfileNotice('');
    const requestedBy = member.email;

    try {
      const saved = await saveProfile({
        name: draft.name.trim(),
        jobRole: draft.jobRole,
        jobRoleTag: draft.jobRoleTag.trim(),
        topTimeTask: joinTimeSink(draft.topTimeSink, draft.topTimeSinkOther),
        automationLevel: draft.automationLevel,
      });
      if (useSession.getState().member?.email !== requestedBy) return;
      useSession.getState().applyOnboarding(saved);
      setEditingProfile(false);
      setProfileNotice('프로필이 저장되었습니다.');
    } catch {
      if (useSession.getState().member?.email === requestedBy) {
        setProfileNotice('프로필을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
      }
    } finally {
      isSavingProfile.current = false;
    }
  };

  return (
    <section aria-labelledby="account-profile-heading" className="flex flex-col gap-3">
      <h2
        id="account-profile-heading"
        className="text-[1.0625rem] font-semibold tracking-[-0.03em]"
      >
        커뮤니티 프로필
      </h2>

      {member.isOnboarded ? (
        isEditingProfile && draft && profileOwner.current === member.email ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submitProfile();
            }}
          >
            <fieldset
              disabled={isProfilePending}
              className="flex flex-col gap-5 rounded-lg border border-border p-5 disabled:opacity-60"
            >
              <NicknameField
                value={{
                  jobRole: draft.jobRole,
                  jobRoleTag: draft.jobRoleTag,
                  handle: draft.name,
                }}
                onChange={(value) =>
                  patchDraft({
                    jobRole: value.jobRole,
                    jobRoleTag: value.jobRoleTag,
                    name: value.handle,
                  })
                }
                idPrefix="account-nickname"
              />
              <label className="flex flex-col gap-1.5 text-[13px]">
                가장 지겨운 반복 업무
                <select
                  aria-label="가장 지겨운 반복 업무"
                  value={draft.topTimeSink}
                  onChange={(event) => patchDraft({ topTimeSink: event.target.value })}
                  className="h-11 rounded-md border border-border bg-card px-3"
                >
                  <option value="">선택해 주세요</option>
                  {[...TIME_SINKS, OTHER].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
              {draft.topTimeSink === OTHER ? (
                <input
                  aria-label="반복 업무 직접 입력"
                  value={draft.topTimeSinkOther}
                  maxLength={200}
                  onChange={(event) => patchDraft({ topTimeSinkOther: event.target.value })}
                  className="h-11 rounded-md border border-border bg-card px-3 text-sm"
                  placeholder="반복 업무를 적어 주세요"
                />
              ) : null}
              <label className="flex flex-col gap-1.5 text-[13px]">
                자동화 경험
                <select
                  aria-label="자동화 경험"
                  value={draft.automationLevel}
                  onChange={(event) => patchDraft({ automationLevel: event.target.value })}
                  className="h-11 rounded-md border border-border bg-card px-3"
                >
                  <option value="">선택해 주세요</option>
                  {AUTOMATION_LEVELS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={
                    isProfilePending ||
                    !isNicknameReady({
                      jobRole: draft.jobRole,
                      jobRoleTag: draft.jobRoleTag,
                      handle: draft.name,
                    }) ||
                    !draft.topTimeSink ||
                    (draft.topTimeSink === OTHER && !draft.topTimeSinkOther.trim()) ||
                    !draft.automationLevel
                  }
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  프로필 저장
                </button>
                <button
                  type="button"
                  disabled={isProfilePending}
                  onClick={() => {
                    setEditingProfile(false);
                    setDraft(null);
                    profileOwner.current = null;
                  }}
                  className="rounded-md border border-border px-4 py-2 text-sm"
                >
                  취소
                </button>
              </div>
            </fieldset>
          </form>
        ) : (
          <div className="flex flex-col gap-4 rounded-lg border border-border p-5 text-[13.5px]">
            <dl className="flex flex-col gap-2">
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 text-muted-foreground">직무</dt>
                <dd>{member.jobRole === OTHER ? member.jobRoleTag : member.jobRole}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 text-muted-foreground">반복 업무</dt>
                <dd>
                  {member.topTimeSink === OTHER ? member.topTimeSinkOther : member.topTimeSink}
                </dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-24 shrink-0 text-muted-foreground">자동화 경험</dt>
                <dd>{member.automationLevel}</dd>
              </div>
            </dl>
            <button
              type="button"
              onClick={startProfileEdit}
              className="self-start rounded-md border border-border px-4 py-2 text-sm"
            >
              프로필 수정
            </button>
          </div>
        )
      ) : (
        <p className="text-[13px] leading-[1.75] text-muted-foreground">
          닉네임과 직무를 아직 입력하지 않으셨습니다.{' '}
          <Link
            to="/onboarding?next=%2Fmy%2Faccount"
            className="underline underline-offset-4 hover:text-foreground"
          >
            입력하기
          </Link>
        </p>
      )}
      {profileNotice ? (
        <p role="status" className="text-[13px]">
          {profileNotice}
        </p>
      ) : null}
    </section>
  );
}
